import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
    Alert,
    Button,
    Checkbox,
    Collapse,
    Input,
    Modal,
    Select,
    Space,
    Steps,
    Table,
    Tag,
    Typography,
} from 'antd';

import {
    ERROR_SCHEME_EXISTS,
    FIELD_GUESS,
    IUtmAnalyzedLabel,
    IUtmDraftField,
    IUtmLabelOption,
    IUtmNameOption,
    IUtmPreview,
    IUtmPreviewSample,
    IUtmScheme,
    IUtmSchemeConflict,
    IUtmSchemeDraft,
    LIMITS,
    PARSER_TYPE,
    PARSER_TYPE_LABELS,
    TYPES_WITH_KV_SEPARATOR,
    pluralizeValues,
} from './types';
import { formatNumberWithSpaces } from '../../../../../helpers/utils/functions';
import { getApiErrorMessage, showSuccessMessage } from '../../../../../helpers/utils/ui';
import { utmSchemesApi } from '../../../../../shared/common/api/endpoints/utmSchemesApi';
import { useTypedSelector } from '../../../../../shared/common/hooks/useTypedSelector';

interface ISchemeEditorProps {
    visible: boolean;
    cabinetId: number;
    cabinetName: string;
    /** Схема кабинета при правке; null — создаём новую по ссылке */
    scheme: IUtmScheme | null;
    /** Справочник параметров из GET /utm-schemes/list */
    utmNames: IUtmNameOption[];
    /** Справочник меток, в которых бывает упакованное значение */
    labels: IUtmLabelOption[];
    /**
     * По школе уже идёт переразбор. Бэкенд проверяет это только в reparse, а create/update
     * поставят вторую задачу молча — поэтому «Сохранить и переразобрать» блокируем сами.
     */
    taskPending?: boolean;
    onClose: () => void;
    /** Схема сохранена; task — поставленная задача переразбора, если просили */
    onSaved: (taskId: number | null) => void;
}

/** Пустой черновик — заполняется разбором ссылки */
const EMPTY_DRAFT: IUtmSchemeDraft = {
    name: '',
    utm_name_id: null,
    parser_type: PARSER_TYPE.alternating,
    separator: '|',
    kv_separator: null,
    signature: '',
    fields: [],
};

/** Схема из базы → черновик редактора */
const schemeToDraft = (scheme: IUtmScheme): IUtmSchemeDraft => ({
    name: scheme.name,
    utm_name_id: scheme.utm_name_id ?? null,
    parser_type: scheme.parser_type,
    separator: scheme.separator,
    kv_separator: scheme.kv_separator,
    signature: scheme.signature,
    fields: (scheme.fields ?? []).map((field) => ({ ...field })),
});

/** Подпись и цвет пометки о происхождении догадки */
const guessTag = (guess?: string) => {
    if (guess === FIELD_GUESS.substitution) {
        return <Tag color="green">из подстановки</Tag>;
    }
    if (guess === FIELD_GUESS.value) {
        return <Tag color="orange">проверьте</Tag>;
    }
    return null;
};

/**
 * Чья схема конфликтует по сигнатуре. Бэкенд отдаёт ровно два значения `scope`:
 * `global` — схема общей библиотеки, `cabinet` — схема другого кабинета этой же школы.
 * Чужих школ в конфликтах не бывает; незнакомое значение не подписываем.
 */
const conflictScopeLabel = (scope?: string): string => {
    switch (scope) {
        case 'global':
            return 'схема из общей библиотеки';
        case 'cabinet':
            return 'схема другого кабинета этой школы';
        default:
            return '';
    }
};

/**
 * Редактор схемы разбора: ссылка → поля → предпросмотр.
 *
 * Создание начинается со вставки ссылки из рекламного кабинета — шаблона с подстановками
 * или реальной ссылки перехода; бэкенд разбирает её в черновик, человек правит параметры.
 * Правка существующей схемы открывается сразу на шаге полей.
 */
const SchemeEditor: FC<ISchemeEditorProps> = ({
    visible,
    cabinetId,
    cabinetName,
    scheme,
    utmNames,
    labels,
    taskPending = false,
    onClose,
    onSaved,
}) => {
    const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
    const schoolId = selectedSchool?.id;
    const isEdit = Boolean(scheme);

    const [ step, setStep ] = useState(0);
    const [ busy, setBusy ] = useState(false);
    const [ error, setError ] = useState('');

    const [ url, setUrl ] = useState('');
    const [ analyzedLabels, setAnalyzedLabels ] = useState<IUtmAnalyzedLabel[]>([]);
    const [ draft, setDraft ] = useState<IUtmSchemeDraft>(EMPTY_DRAFT);
    const [ conflicts, setConflicts ] = useState<IUtmSchemeConflict[]>([]);
    const [ preview, setPreview ] = useState<IUtmPreview | null>(null);
    // utm_source из разобранной ссылки — бэкенд хранит его в схеме
    const [ utmSourceValue, setUtmSourceValue ] = useState<string | null>(null);

    // Правка открывается сразу на полях, создание — с шага ссылки
    useEffect(() => {
        if (!visible) return;

        setError('');
        setPreview(null);
        setConflicts([]);
        if (scheme) {
            setDraft(schemeToDraft(scheme));
            setUrl(scheme.sample_url ?? '');
            setUtmSourceValue(scheme.utm_source_value ?? null);
            setStep(1);
        } else {
            setDraft(EMPTY_DRAFT);
            setUrl('');
            setAnalyzedLabels([]);
            setStep(0);
        }
    }, [ visible, scheme ]);

    const setDraftField = (index: number, patch: Partial<IUtmDraftField>) => {
        setDraft((current) => ({
            ...current,
            fields: current.fields.map((field, i) => (i === index ? { ...field, ...patch } : field)),
        }));
    };

    /** Параметры, назначенные больше одному полю: бэкенд такое не примет */
    const duplicateParams = useMemo(() => {
        const seen = new Map<number, number>();
        const duplicates = new Set<number>();

        draft.fields.forEach((field) => {
            if (field.is_ignored || !field.utm_name_id) return;
            const count = (seen.get(field.utm_name_id) ?? 0) + 1;
            seen.set(field.utm_name_id, count);
            if (count > 1) duplicates.add(field.utm_name_id);
        });

        return duplicates;
    }, [ draft.fields ]);

    const usedParamsCount = draft.fields.filter((f) => !f.is_ignored && f.utm_name_id).length;
    // Поле оставлено «в работе», но параметр не выбран — бэкенд такое отклонит
    const emptyParamKeys = draft.fields
        .filter((field) => !field.is_ignored && !field.utm_name_id)
        .map((field) => field.field_key);
    const needsKvSeparator = TYPES_WITH_KV_SEPARATOR.includes(draft.parser_type);

    /** Сигнатура — регулярное выражение; проверяем до отправки, бэкенд делает то же самое */
    const signatureValid = useMemo(() => {
        const signature = draft.signature.trim();
        if (signature === '') return false;
        try {
            // eslint-disable-next-line no-new
            new RegExp(signature);
            return true;
        } catch {
            return false;
        }
    }, [ draft.signature ]);

    const fieldsValid =
        draft.name.trim() !== ''
        && signatureValid
        && (draft.separator ?? '') !== ''
        && (!needsKvSeparator || (draft.kv_separator ?? '') !== '')
        && usedParamsCount > 0
        && emptyParamKeys.length === 0
        && duplicateParams.size === 0;

    const analyze = async () => {
        if (!schoolId || !url.trim()) return;

        setBusy(true);
        setError('');
        try {
            const response = await utmSchemesApi.analyzeUrl(schoolId, cabinetId, url.trim());
            const payload = response.data?.data ?? {};
            const foundLabels: IUtmAnalyzedLabel[] = Array.isArray(payload.labels) ? payload.labels : [];
            setAnalyzedLabels(foundLabels);
            setUtmSourceValue(payload.utm_source_value ?? null);

            const packed = foundLabels.find((label) => label.packed && label.draft);
            if (!packed?.draft) {
                setError('В ссылке не найдено упакованных меток. Обычные UTM-метки уже учитываются в отчётах.');
                return;
            }
            setDraft({ ...packed.draft, fields: packed.draft.fields.map((field) => ({ ...field })) });
            setConflicts(packed.draft.conflicts ?? []);
            setStep(1);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось разобрать ссылку'));
        } finally {
            setBusy(false);
        }
    };

    /** Черновик в том виде, в каком его ждут preview и create/update */
    const draftPayload = useCallback(() => ({
        name: draft.name.trim(),
        utm_name_id: draft.utm_name_id,
        parser_type: draft.parser_type,
        separator: draft.separator,
        kv_separator: needsKvSeparator ? draft.kv_separator : null,
        signature: draft.signature.trim(),
        fields: draft.fields.map((field, index) => ({
            field_key: field.field_key,
            utm_name_id: field.is_ignored ? null : field.utm_name_id,
            is_ignored: Boolean(field.is_ignored),
            sort_order: field.sort_order ?? index + 1,
        })),
    }), [ draft, needsKvSeparator ]);

    const runPreview = async () => {
        if (!schoolId) return;

        setBusy(true);
        setError('');
        try {
            const response = await utmSchemesApi.preview(schoolId, draftPayload());
            setPreview((response.data?.data ?? null) as IUtmPreview | null);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось получить предпросмотр'));
        } finally {
            setBusy(false);
        }
    };

    const save = async (reparse: boolean) => {
        if (!schoolId) return;

        setBusy(true);
        setError('');
        try {
            const payload = {
                ...draftPayload(),
                school_id: schoolId,
                sample_url: url.trim() || null,
                utm_source_value: utmSourceValue,
                reparse,
            };
            const response = scheme
                ? await utmSchemesApi.update(scheme.id, payload)
                : await utmSchemesApi.create({ ...payload, integration_id: cabinetId });

            showSuccessMessage(scheme ? 'Схема сохранена' : 'Схема создана');
            onSaved(response.data?.data?.task_id ?? null);
        } catch (err) {
            const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
            setError(
                code === ERROR_SCHEME_EXISTS
                    // Схема появилась, пока окно было открыто (вторая вкладка) — список нужно перечитать
                    ? 'У кабинета уже настроен разбор параметров. Закройте окно и откройте заново, чтобы изменить существующую схему.'
                    : getApiErrorMessage(err, 'Не удалось сохранить схему')
            );
        } finally {
            setBusy(false);
        }
    };

    const fieldColumns = [
        {
            title: 'Поле в ссылке',
            dataIndex: 'field_key',
            key: 'field_key',
            width: 130,
        },
        {
            title: 'Значение из ссылки',
            dataIndex: 'raw',
            key: 'raw',
            width: 180,
            render: (raw?: string) => raw || <span style={{ color: '#bfbfbf' }}>—</span>,
        },
        {
            title: 'Параметр',
            key: 'utm_name_id',
            render: (_: unknown, field: IUtmDraftField, index: number) => (
                <Space>
                    <Select
                        style={{ width: 220 }}
                        value={field.is_ignored ? undefined : field.utm_name_id ?? undefined}
                        placeholder="Выберите параметр"
                        disabled={field.is_ignored}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        status={
                            !field.is_ignored
                            && (
                                !field.utm_name_id
                                || duplicateParams.has(field.utm_name_id)
                            )
                                ? 'error'
                                : undefined
                        }
                        onChange={(value) => setDraftField(index, { utm_name_id: value ?? null })}
                    >
                        {utmNames.map((option) => (
                            <Select.Option key={option.id} value={option.id}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                    {!field.is_ignored && guessTag(field.guess)}
                </Space>
            ),
        },
        {
            title: 'Не использовать',
            key: 'is_ignored',
            width: 130,
            render: (_: unknown, field: IUtmDraftField, index: number) => (
                <Checkbox
                    checked={Boolean(field.is_ignored)}
                    onChange={(event) =>
                        setDraftField(index, {
                            is_ignored: event.target.checked,
                            utm_name_id: event.target.checked ? null : field.utm_name_id,
                        })
                    }
                />
            ),
        },
    ];

    const previewColumns = [
        {
            title: 'Значение метки',
            dataIndex: 'value',
            key: 'value',
            // Ширина в процентах вместе с tableLayout="fixed": упакованное значение —
            // одно длинное «слово», без break-all таблица раздувается и вылезает из окна
            width: '45%',
            render: (value: string, sample: IUtmPreviewSample) => (
                <span
                    style={{
                        color: sample.status === 'matched' ? undefined : '#ad6800',
                        wordBreak: 'break-all',
                        fontSize: 12,
                    }}
                >
                    {value}
                </span>
            ),
        },
        {
            title: 'Разобранные параметры',
            key: 'fields',
            width: '55%',
            render: (_: unknown, sample: IUtmPreviewSample) => {
                if (!sample.fields.length) {
                    return <span style={{ color: '#ad6800' }}>ничего не разобрано</span>;
                }
                return (
                    <div>
                        {/* Параметров бывает под два десятка — раскладываем в строку с переносом,
                            иначе строка таблицы вытягивается на пол-экрана */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px' }}>
                            {sample.fields.map((field) => (
                                <span key={field.utm_name_id} style={{ wordBreak: 'break-word' }}>
                                    <b>{field.label}:</b> {field.value}
                                </span>
                            ))}
                        </div>
                        {sample.unknown_keys.length > 0 && (
                            <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 4 }}>
                                нет в схеме: {sample.unknown_keys.join(', ')}
                            </div>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <Modal
            title={`${isEdit ? 'Изменение' : 'Настройка'} разбора параметров — ${cabinetName}`}
            visible={visible}
            onCancel={onClose}
            footer={null}
            width={900}
            style={{ top: 40 }}
            bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}
            destroyOnClose
        >
            <Steps current={step} size="small" style={{ marginBottom: 20 }}>
                <Steps.Step title="Ссылка" />
                <Steps.Step title="Поля" />
                <Steps.Step title="Предпросмотр" />
            </Steps>

            {error && (
                <Alert type="error" message={error} style={{ marginBottom: 16 }} showIcon />
            )}

            {step === 0 && (
                <>
                    <Typography.Paragraph>
                        Вставьте ссылку из рекламного кабинета: шаблон с подстановками
                        или реальную ссылку перехода.
                    </Typography.Paragraph>
                    <Input.TextArea
                        rows={3}
                        maxLength={LIMITS.url}
                        value={url}
                        onChange={(event) => setUrl(event.target.value)}
                        placeholder="https://site.ru/?utm_source=yandex&utm_campaign=1sn|yd|2sn|{campaign_id}|..."
                    />

                    {analyzedLabels.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                            <Typography.Text type="secondary">Найденные метки:</Typography.Text>
                            {analyzedLabels.map((label) => (
                                <div key={label.utm_name}>
                                    <b>{label.utm_name}</b> = {label.value}
                                    {label.packed && <Tag color="blue" style={{ marginLeft: 8 }}>упакована</Tag>}
                                </div>
                            ))}
                        </div>
                    )}

                    <Space style={{ marginTop: 16 }}>
                        <Button type="primary" onClick={analyze} loading={busy} disabled={!url.trim()}>
                            Разобрать
                        </Button>
                        <Button onClick={onClose}>Отмена</Button>
                    </Space>
                </>
            )}

            {step === 1 && (
                <>
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <Space wrap>
                            <Input
                                style={{ width: 320 }}
                                maxLength={LIMITS.name}
                                value={draft.name}
                                placeholder="Название схемы"
                                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                            />
                            <Select
                                style={{ width: 200 }}
                                value={draft.utm_name_id ?? undefined}
                                placeholder="Метка поиска"
                                allowClear
                                onChange={(value) => setDraft({ ...draft, utm_name_id: value ?? null })}
                            >
                                {labels.map((label) => (
                                    <Select.Option key={label.id} value={label.id}>
                                        {label.utm_name}
                                    </Select.Option>
                                ))}
                            </Select>
                            <Select
                                style={{ width: 180 }}
                                value={draft.parser_type}
                                onChange={(value) => setDraft({ ...draft, parser_type: value })}
                            >
                                {Object.entries(PARSER_TYPE_LABELS).map(([ value, label ]) => (
                                    <Select.Option key={value} value={Number(value)}>
                                        {label}
                                    </Select.Option>
                                ))}
                            </Select>
                            <Input
                                style={{ width: 120 }}
                                maxLength={LIMITS.separator}
                                value={draft.separator ?? ''}
                                placeholder="Разделитель"
                                onChange={(event) => setDraft({ ...draft, separator: event.target.value })}
                            />
                            {needsKvSeparator && (
                                <Input
                                    style={{ width: 160 }}
                                    maxLength={LIMITS.separator}
                                    value={draft.kv_separator ?? ''}
                                    placeholder="Имя:значение"
                                    onChange={(event) => setDraft({ ...draft, kv_separator: event.target.value })}
                                />
                            )}
                        </Space>

                        <Collapse ghost>
                            <Collapse.Panel header="Расширенные настройки" key="advanced">
                                <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                                    Сигнатура — признак, по которому система узнаёт метку этого формата.
                                </Typography.Paragraph>
                                <Input
                                    maxLength={LIMITS.signature}
                                    value={draft.signature}
                                    status={draft.signature.trim() !== '' && !signatureValid ? 'error' : undefined}
                                    onChange={(event) => setDraft({ ...draft, signature: event.target.value })}
                                />
                                {draft.signature.trim() !== '' && !signatureValid && (
                                    <Typography.Text type="danger">
                                        Сигнатура не является корректным регулярным выражением
                                    </Typography.Text>
                                )}
                            </Collapse.Panel>
                        </Collapse>

                        {conflicts.length > 0 && (
                            <Alert
                                type="info"
                                showIcon
                                message="Похожие значения уже разбирает другая схема"
                                description={
                                    <>
                                        <ul style={{ margin: '0 0 8px', paddingLeft: 18 }}>
                                            {conflicts.map((conflict) => (
                                                <li key={conflict.scheme_id}>
                                                    {conflict.name}
                                                    {conflictScopeLabel(conflict.scope) && (
                                                        <span style={{ color: '#8c8c8c' }}>
                                                            {' — '}
                                                            {conflictScopeLabel(conflict.scope)}
                                                        </span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                        Значение разбирает первая схема, чья сигнатура совпала. Сначала
                                        примеряются схемы кабинетов школы, потом общая библиотека, поэтому
                                        библиотечная схема получит только то, что не подошло под вашу.
                                        {conflicts.some((conflict) => conflict.scope === 'cabinet') && (
                                            <>
                                                {' '}У Яндекс.Директа формат меток общий для всех кабинетов, поэтому
                                                схема другого кабинета — не ошибка: одинаковые значения разберёт та
                                                из них, что создана раньше. Данные не потеряются.
                                            </>
                                        )}
                                    </>
                                }
                            />
                        )}

                        {duplicateParams.size > 0 && (
                            <Alert
                                type="error"
                                showIcon
                                message="Один параметр назначен двум полям — оставьте его только у одного"
                            />
                        )}

                        {emptyParamKeys.length > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                message={`Выберите параметр или отметьте «не использовать»: ${emptyParamKeys.join(', ')}`}
                            />
                        )}

                        <Table
                            rowKey="field_key"
                            size="small"
                            dataSource={draft.fields}
                            columns={fieldColumns}
                            pagination={false}
                            locale={{ emptyText: 'Полей нет — вернитесь к ссылке и разберите её заново' }}
                        />
                    </Space>

                    <Space style={{ marginTop: 16 }}>
                        {!isEdit && <Button onClick={() => setStep(0)}>Назад</Button>}
                        <Button type="primary" onClick={() => setStep(2)} disabled={!fieldsValid}>
                            Далее
                        </Button>
                    </Space>
                </>
            )}

            {step === 2 && (
                <>
                    <Space style={{ marginBottom: 16 }}>
                        <Button onClick={runPreview} loading={busy}>
                            Проверить на моих данных
                        </Button>
                    </Space>

                    {preview && (
                        <>
                            <Typography.Paragraph>
                                Под схему подходит {preview.matched_total_capped ? 'более ' : ''}
                                {formatNumberWithSpaces(preview.matched_total)} значений школы.
                                {preview.samples.length > 0
                                    && ` Ниже — ${preview.samples.length} ${pluralizeValues(preview.samples.length)} для проверки, весь список не выводится:`}
                            </Typography.Paragraph>
                            <Table
                                rowKey={(sample: IUtmPreviewSample) => String(sample.utm_filter_id ?? sample.value)}
                                size="small"
                                tableLayout="fixed"
                                dataSource={preview.samples}
                                columns={previewColumns}
                                pagination={false}
                                scroll={{ y: 320 }}
                                locale={{ emptyText: 'Подходящих значений не нашлось' }}
                            />
                        </>
                    )}

                    <Space style={{ marginTop: 16 }}>
                        <Button onClick={() => setStep(1)}>Назад</Button>
                        <Button onClick={() => save(false)} loading={busy} disabled={!fieldsValid}>
                            Сохранить
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => save(true)}
                            loading={busy}
                            disabled={!fieldsValid || taskPending}
                        >
                            Сохранить и переразобрать
                        </Button>
                    </Space>
                    <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
                        {taskPending
                            ? 'По школе уже идёт переразбор — схему можно сохранить, а применить её ко всей истории получится после его завершения.'
                            : 'Переразбор применит схему ко всей истории и займёт до нескольких минут.'}
                    </Typography.Paragraph>
                </>
            )}
        </Modal>
    );
};

export default SchemeEditor;
